import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { asTextArray, buildLearningTopicPath } from '../../lib/educationContent';
import LearningFeedbackButtons from '../../components/education/LearningFeedbackButtons';
import LearningList from '../../components/education/LearningList';
import TopicContentSections from '../../components/education/TopicContentSections';
import { trackLearningEvent } from '../../lib/learningEvents';

const TopicPage = () => {
  const { slug } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matterId = searchParams.get('matterId');
  const questionKey = searchParams.get('questionKey');

  const [topic, setTopic] = useState(null);
  const [resources, setResources] = useState([]);
  const [relatedTopics, setRelatedTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: topicData, error: topicError } = await supabase
          .from('education_topics')
          .select('*')
          .eq('slug', slug)
          .eq('publication_status', 'published')
          .single();

        if (topicError || !topicData) throw new Error('Learning topic not found.');
        setTopic(topicData);

        const [{ data: resourceLinks }, { data: relationships }] = await Promise.all([
          supabase
            .from('education_topic_resources')
            .select('relationship_type, display_priority, resource:education_resources(*)')
            .eq('topic_id', topicData.id)
            .eq('is_active', true)
            .order('display_priority', { ascending: true }),
          supabase
            .from('education_topic_relationships')
            .select('relationship_type, display_priority, to_topic_id')
            .eq('from_topic_id', topicData.id)
            .order('display_priority', { ascending: true })
        ]);

        setResources((resourceLinks || []).filter((link) => link.resource?.is_active));

        const relatedIds = (relationships || []).map((row) => row.to_topic_id).filter(Boolean);
        if (relatedIds.length > 0) {
          const { data: relatedData } = await supabase
            .from('education_topics')
            .select('id, title, slug, short_summary, content_category')
            .in('id', relatedIds)
            .eq('publication_status', 'published');

          const relatedById = {};
          (relatedData || []).forEach((row) => {
            relatedById[row.id] = row;
          });

          setRelatedTopics((relationships || [])
            .map((relationship) => ({
              ...relationship,
              topic: relatedById[relationship.to_topic_id]
            }))
            .filter((relationship) => relationship.topic));
        } else {
          setRelatedTopics([]);
        }

        if (profile?.id) {
          await trackLearningEvent({
            userId: profile.id,
            matterId,
            questionKey,
            topicId: topicData.id,
            eventType: 'topic_viewed',
            metadata: {
              source: matterId ? 'guided_intake_learn_more' : 'learning_topic_page',
              topic_slug: topicData.slug
            }
          });
        }
      } catch (err) {
        console.error('Topic page load error:', err);
        setError(err.message || 'Unable to load this learning topic.');
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug, profile?.id, matterId, questionKey]);

  const openResource = async (resource) => {
    if (topic?.id && profile?.id) {
      await trackLearningEvent({
        userId: profile.id,
        matterId,
        questionKey,
        topicId: topic.id,
        resourceId: resource.id,
        eventType: 'resource_opened',
        metadata: {
          source: 'learning_topic_page',
          resource_slug: resource.slug
        }
      });
    }
    const params = new URLSearchParams();
    if (matterId) params.set('matterId', matterId);
    if (questionKey) params.set('questionKey', questionKey);
    if (topic?.id) params.set('topicId', topic.id);
    const query = params.toString();
    navigate(`/education/${resource.slug}${query ? `?${query}` : ''}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="h-10 w-10 animate-spin text-slate-300" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-20 max-w-xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <SafeIcon icon={FiIcons.FiSearch} className="mx-auto mb-4 h-12 w-12 text-slate-300" />
        <h2 className="text-xl font-bold text-slate-900">Topic not found</h2>
        <p className="mt-2 text-sm text-slate-500">{error}</p>
        <button onClick={() => navigate(-1)} className="mt-6 text-xs font-bold uppercase tracking-widest text-blue-600">Go Back</button>
      </div>
    );
  }

  const advisorQuestions = asTextArray(topic.advisor_discussion_questions_json);
  const attorneyQuestions = asTextArray(topic.attorney_discussion_questions_json);

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-20">
      <button onClick={() => navigate(-1)} className="flex items-center text-slate-400 transition-colors hover:text-slate-600">
        <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2" /> Back
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-blue-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
            {topic.content_category}
          </span>
          <span className="text-xs font-medium text-slate-400">Guided Learning</span>
        </div>
        <h1 className="mt-5 text-3xl font-bold leading-tight text-slate-900 md:text-4xl">{topic.title}</h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-600">{topic.short_summary}</p>

        <LearningFeedbackButtons
          topic={topic}
          matterId={matterId}
          questionKey={questionKey}
          userId={profile?.id}
          source="learning_topic_page"
          className="mt-6"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <TopicContentSections topic={topic} />
        </div>

        <div className="space-y-6">
          {(advisorQuestions.length > 0 || attorneyQuestions.length > 0) && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-blue-800">Questions to Discuss</h2>
              {advisorQuestions.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">With Your Advisor</p>
                  <div className="mt-2 text-sm text-slate-700">
                    <LearningList items={advisorQuestions} />
                  </div>
                </div>
              )}
              {attorneyQuestions.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">With Your Attorney</p>
                  <div className="mt-2 text-sm text-slate-700">
                    <LearningList items={attorneyQuestions} />
                  </div>
                </div>
              )}
            </div>
          )}

          {resources.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-800">Related Resources</h2>
              <div className="mt-4 space-y-3">
                {resources.map(({ resource, relationship_type }) => (
                  <button
                    key={`${resource.id}-${relationship_type}`}
                    type="button"
                    onClick={() => openResource(resource)}
                    className="w-full rounded-lg border border-slate-200 p-4 text-left transition-colors hover:border-blue-200 hover:bg-blue-50/40"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        {resource.resource_type}
                      </span>
                      <span className="text-xs text-slate-400">{resource.estimated_minutes || 5} min</span>
                    </div>
                    <p className="mt-2 text-sm font-bold text-slate-900">{resource.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{resource.short_description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {relatedTopics.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-800">Related Topics</h2>
              <div className="mt-4 space-y-3">
                {relatedTopics.map(({ topic: relatedTopic, relationship_type }) => (
                  <button
                    key={`${relatedTopic.id}-${relationship_type}`}
                    type="button"
                    onClick={() => navigate(buildLearningTopicPath({ slug: relatedTopic.slug, matterId, questionKey }))}
                    className="w-full rounded-lg border border-slate-200 p-4 text-left transition-colors hover:border-blue-200 hover:bg-blue-50/40"
                  >
                    <p className="text-sm font-bold text-slate-900">{relatedTopic.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{relatedTopic.short_summary}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default TopicPage;
